import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { Category } from "../../../api/types/accessTypes";
import { defaultBrandIconName } from "./brandIconOptions";
import IconPicker from "./IconPicker";
import TagColorPicker from "./TagColorPicker";

const nameMaxLength = 50;
const descriptionMaxLength = 500;

const createAccessCategory = withErrorHandling(adminApi.createAccessCategory);
const updateAccessCategory = withErrorHandling(adminApi.updateAccessCategory);

interface Props {
  category?: Category;
  initialTagColor?: string;
  investorPortalEnabled: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const EditAccessCategoryDialog = ({ category, initialTagColor, investorPortalEnabled, onClose, onSaved }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [name, setName] = useState(category?.name ?? "");
  const [tagColor, setTagColor] = useState(category?.color ?? initialTagColor);
  const [iconName, setIconName] = useState(category?.iconName || defaultBrandIconName);
  const [showIcon, setShowIcon] = useState(category ? category.showIcon : true);
  const [description, setDescription] = useState(category?.description ?? "");
  const [saving, setSaving] = useState(false);

  const isNew = category === undefined;
  const showIconChecked = showIcon ?? true;
  const trimmedName = name.trim();
  const trimmedDescription = description.trim();

  const isDirty =
    isNew ||
    trimmedName !== category.name ||
    tagColor !== category.color ||
    iconName !== category.iconName ||
    showIcon !== category.showIcon ||
    trimmedDescription !== (category.description ?? "");

  const handleSaveClick = async () => {
    setSaving(true);
    const [, error] = category
      ? await updateAccessCategory(category.id, {
          type: category.type,
          name: trimmedName,
          color: tagColor,
          iconName,
          showIcon,
          descriptionUserInput: trimmedDescription || undefined,
        })
      : await createAccessCategory({
          type: "Message",
          name: trimmedName,
          color: tagColor,
          iconName,
          showIcon,
          descriptionUserInput: trimmedDescription || undefined,
        });
    setSaving(false);

    if (error) {
      logError(error, "[EditAccessCategoryDialog] saveAccessCategory");
      sendNotificationError(error.message);
      return;
    }

    sendNotification(isNew ? "Category created successfully." : "Category updated successfully.");
    onSaved();
  };

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>{isNew ? "New Category" : "Edit Category"}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="subtitle2">Category Setting</Typography>
          <TextField
            label="Name"
            value={name}
            fullWidth
            autoFocus={isNew}
            slotProps={{
              htmlInput: { maxLength: nameMaxLength },
              input: {
                endAdornment: (
                  <Typography variant="caption" color="textSecondary">
                    {Math.max(0, nameMaxLength - name.length)}
                  </Typography>
                ),
              },
            }}
            onChange={(e) => setName(e.target.value)}
          />

          <TagColorPicker categoryName={trimmedName} value={tagColor} onChange={setTagColor} />

          {!investorPortalEnabled && (
            <Alert severity="info">
              Investor Portal isn&apos;t enabled for this client, so the inbox icon and description don&apos;t apply.
              The tag color is still used here in the Admin Portal.
            </Alert>
          )}

          {investorPortalEnabled && (
            <>
              <Divider />
              <Typography variant="subtitle2">Investor Portal</Typography>

              <Stack>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <Switch checked={showIconChecked} onChange={(_, checked) => setShowIcon(checked)} />
                  <Typography>Show Inbox Icon</Typography>
                </Stack>
                <Typography variant="caption" color="textSecondary" sx={{ pl: 6 }}>
                  The icon shown for this category in the investor portal inbox menu.
                </Typography>
              </Stack>

              {showIconChecked && <IconPicker categoryName={trimmedName} value={iconName} onChange={setIconName} />}

              <Stack spacing={0.75}>
                <Typography variant="subtitle2">Description</Typography>
                <Typography variant="caption" color="textSecondary">
                  This description is shown to the contact who manages category access for other contacts on the
                  investor portal.
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="Add description (optional)"
                  value={description}
                  slotProps={{ htmlInput: { maxLength: descriptionMaxLength } }}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelDisabled={saving}>
        <Button variant="contained" loading={saving} disabled={!isDirty || !trimmedName} onClick={handleSaveClick}>
          Save
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default EditAccessCategoryDialog;
